import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, Loader2, Paperclip, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "General Feedback", "Usability", "Diagnostic Questions", "Portfolio Manager Portal",
  "School User Portal", "Sales Portal", "Super Admin", "Reporting", "Performance",
  "Bug / Error", "Feature Request", "Other",
];

/**
 * Shared "Give Us Feedback" modal, Clarity-styled. Works for both auth models:
 *  - Session portals (Admin/Sales/Portfolio): pass nothing → uses cookies.
 *  - School users (header auth): pass `authHeaders` from the diag session.
 */
export const FeedbackModal = ({ open, onOpenChange, authHeaders, portalType }) => {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("General Feedback");
  const [page, setPage] = useState("");
  const [contactOk, setContactOk] = useState(false);
  const [attachment, setAttachment] = useState(null); // {url, name}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reqCfg = authHeaders ? { headers: authHeaders } : { withCredentials: true };

  const reset = () => {
    setText(""); setCategory("General Feedback"); setPage(""); setContactOk(false); setAttachment(null);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) { toast.error("Use a PNG, JPG, or WebP image."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/api/assets/feedback-attachment`, fd, reqCfg);
      setAttachment({ url: r.data.url, name: f.name });
      toast.success("Screenshot attached");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Please enter your feedback."); return; }
    setSaving(true);
    try {
      const payload = {
        feedback_text: text.trim(),
        feedback_category: category,
        page_feature: page || null,
        current_route: window.location.pathname,
        current_url: window.location.href,
        contact_permission: contactOk,
        attachment_url: attachment?.url || null,
        portal_type: portalType || null,
        user_agent: navigator.userAgent,
        application_version: process.env.REACT_APP_VERSION || null,
      };
      const r = await axios.post(`${API}/api/feedback`, payload, reqCfg);
      if (r.data.email_status === "failed") {
        toast.message("Feedback saved", { description: "Your feedback was saved, but the notification email could not be sent. The 589 Labs team can still review your submission." });
      } else {
        toast.success("Thank you! Your feedback has been shared with the 589 Labs team.");
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit feedback.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="feedback-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600">
              <MessageSquareText className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-[17px]">Share feedback about your experience</DialogTitle>
              <DialogDescription>Tell us what worked and what could be improved.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <label className="text-[12px] font-medium text-slate-600">Feedback <span className="text-red-500">*</span></label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
              placeholder="Tell us what worked as expected and what could be better…"
              className="mt-1 resize-none" data-testid="feedback-text" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-[12px] font-medium text-slate-600">Feedback Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="feedback-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`feedback-cat-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[12px] font-medium text-slate-600">Page / Feature</label>
              <Input value={page} onChange={(e) => setPage(e.target.value)} placeholder="Optional"
                className="mt-1" data-testid="feedback-page" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="fb-contact" checked={contactOk} onCheckedChange={(v) => setContactOk(!!v)} data-testid="feedback-contact-permission" />
            <label htmlFor="fb-contact" className="text-[13px] text-slate-600">May we contact you about this feedback?</label>
          </div>

          <div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPickFile} data-testid="feedback-attachment-input" />
            {attachment ? (
              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-600" data-testid="feedback-attachment-chip">
                <Paperclip className="h-4 w-4" /> <span className="truncate flex-1">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="feedback-attach-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Paperclip className="h-4 w-4 mr-1" /> Attach screenshot (optional)</>}
              </Button>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="feedback-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !text.trim()} data-testid="feedback-submit-btn"
            className="bg-[#111827] hover:bg-[#0b1220] text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Share Feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
